import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import axios from 'axios';
import { CheckCircle, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function DonationsSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  const checkPaymentStatus = useCallback(async () => {
    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${API}/donations/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setPaymentDetails(response.data);
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        attempts++;
        setTimeout(poll, pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    poll();
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId, checkPaymentStatus]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <section className="section-padding bg-white" data-testid="donations-success-page">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            {status === 'checking' && (
              <div data-testid="payment-checking">
                <Loader className="animate-spin h-16 w-16 text-[#4A6B48] mx-auto mb-6" />
                <h1 className="text-3xl font-bold text-[#2E332D] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Vérification du paiement...
                </h1>
                <p className="text-[#5C665A]">Veuillez patienter pendant que nous confirmons votre transaction.</p>
              </div>
            )}

            {status === 'success' && (
              <div data-testid="payment-success">
                <CheckCircle className="h-16 w-16 text-[#4A6B48] mx-auto mb-6" />
                <h1 className="text-3xl font-bold text-[#2E332D] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Merci pour votre générosité !
                </h1>
                <p className="text-[#5C665A] mb-6">
                  Votre don de ${paymentDetails?.amount_total?.toFixed(2)} USD a été reçu avec succès.
                </p>
                <p className="text-sm text-[#5C665A] mb-8">
                  Vous recevrez un reçu par email. Votre contribution aidera à transformer nos communautés.
                </p>
                <Link to="/" className="btn-primary inline-block" data-testid="back-home-button">
                  Retour à l&apos;accueil
                </Link>
              </div>
            )}

            {status === 'timeout' && (
              <div data-testid="payment-timeout">
                <h1 className="text-3xl font-bold text-[#2E332D] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Vérification en cours
                </h1>
                <p className="text-[#5C665A] mb-6">
                  Nous vérifions votre paiement. Vous recevrez un email de confirmation sous peu.
                </p>
                <Link to="/" className="btn-primary inline-block">
                  Retour à l&apos;accueil
                </Link>
              </div>
            )}

            {(status === 'error' || status === 'expired') && (
              <div data-testid="payment-error">
                <h1 className="text-3xl font-bold text-[#2E332D] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Une erreur s&apos;est produite
                </h1>
                <p className="text-[#5C665A] mb-6">
                  Votre paiement n&apos;a pas pu être traité. Veuillez réessayer ou nous contacter.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/dons" className="btn-primary">
                    Réessayer
                  </Link>
                  <Link to="/" className="btn-secondary">
                    Retour à l&apos;accueil
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
